import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CBadge,
} from "@coreui/react";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridReadyEvent } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { getList } from "../../api/generics";
import Asset from "../../models/asset";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import { BsFillPlusCircleFill } from "react-icons/bs";
import AssetUpdateModal from "./AssetUpdateModal";
import AssetDeleteModal from "./AssetDeleteModal";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import {
  ASSETS_CREATE,
  ASSETS_DESTROY,
  ASSETS_UPDATE,
} from "../../auth/permissions";
import AssetCopyDocumentsModal from "./AssetCopyDocumentsModal";

const ITEMS_PER_PAGE = 1000;

const spanishLocaleText = {
  // Filter translations
  filterOoo: "Filtrar...",
  equals: "Igual",
  notEqual: "No igual",
  lessThan: "Menor que",
  greaterThan: "Mayor que",
  inRange: "En rango",
  contains: "Contiene",
  notContains: "No contiene",
  startsWith: "Comienza con",
  endsWith: "Termina con",
  andCondition: "Y",
  orCondition: "O",
  applyFilter: "Aplicar",
  resetFilter: "Reiniciar",
  clearFilter: "Limpiar",

  // Other common translations
  page: "Página",
  to: "a",
  of: "de",
  nextPage: "Siguiente página",
  lastPage: "Última página",
  firstPage: "Primera página",
  previousPage: "Página anterior",
  loadingOoo: "Cargando...",
  noRowsToShow: "Sin datos para mostrar",

  // translations for ag-Grid:
  // https://www.ag-grid.com/javascript-data-grid/localisation/
};

const AssetList = () => {
  //const [search, setSearch] = useState<string>("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCopyDocumentsModal, setShowCopyDocumentsModal] = useState(false);
  const [asset, setAsset] = useState<Asset | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState<Asset[]>([]);
  //const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const history = useHistory();
  const companyId = useSelector(
    (state: RootState) => state.company.data.company.id
  );

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }
    let search = urlParams.get("search") ? urlParams.get("search") : "";
    let map = new Map();
    map.set("search", search);
    map.set("page", page);
    return map;
  };

  const fetchAssets = async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.get("page") - 1);
    urlParams.set("company_id", companyId);
    const assetsStatus = await getList<Asset>(
      "/assets/",
      limit,
      offset,
      urlParams
    );
    if (assetsStatus.status === SUCCESS) {
      if (assetsStatus.data !== undefined) {
        const count = assetsStatus.data.count;
        setTotalItems(count || 0);
        setAssets(assetsStatus.data.items);
        //setCurrentPage(urlParams.get("page"));
      }
    } else {
      const message = assetsStatus.detail
        ? assetsStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  };

  const onAddClick = () => {
    setAsset(undefined);
    setShowEditModal(true);
  };

  const onUpdateClick = (asset: Asset) => {
    setAsset(asset);
    setShowEditModal(true);
  };

  const onDeleteClick = (asset: Asset) => {
    setAsset(asset);
    setShowDeleteModal(true);
  };

  const onCopyDocumentsClick = (asset: Asset) => {
    setAsset(asset);
    setShowCopyDocumentsModal(true);
  };

  const onAssetEditCancel = () => {
    setAsset(undefined);
    setShowEditModal(false);
  };

  const onAssetEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowEditModal(false);
    setAssets([]);
    setLoading(true);
    fetchAssets();
  };

  const onAssetDeleteCancel = () => {
    setAsset(undefined);
    setShowDeleteModal(false);
  };

  const onAssetDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowDeleteModal(false);
    setAssets([]);
    setLoading(true);
    fetchAssets();
  };

  const onAssetCopyDocumentsCancel = () => {
    setAsset(undefined);
    setShowCopyDocumentsModal(false);
  };

  const onAssetCopyDocumentsSuccess = () => {
    successAlert("Documentos requeridos actualizados con éxito!");
    setShowCopyDocumentsModal(false);
    setAssets([]);
    setLoading(true);
    fetchAssets();
  };

  /*const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("search", search);
    urlParams.set("page", "1"); // Reset to first page on search
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };*/

  // AG Grid column definitions
  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "Código",
        field: "codeIdentifier",
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        headerName: "Nombre",
        field: "name",
        sortable: true,
        filter: true,
        flex: 2,
      },
      {
        headerName: "Tipo de Activo",
        field: "typeName",
        sortable: true,
        filter: true,
        flex: 1.5,
      },
      {
        headerName: "Subtipo de Activo",
        field: "subtypeName",
        sortable: true,
        filter: true,
        flex: 1.5,
      },
      {
        headerName: "Vigente",
        field: "isActive",
        sortable: true,
        filter: true,
        flex: 1,
        cellRenderer: (params: any) => {
          return params.value ? (
            <CBadge color="success">Vigente</CBadge>
          ) : (
            <CBadge color="secondary">No Vigente</CBadge>
          );
        },
      },
      {
        headerName: "Acciones",
        field: "actions",
        sortable: false,
        filter: false,
        flex: 1.5,
        cellRenderer: (params: any) => {
          const item = params.data as Asset;
          return (
            <CButtonGroup>
              <PermissionRequiredComponent permissionName={ASSETS_CREATE}>
                <CButton
                  className="text-white"
                  color="primary"
                  size="sm"
                  onClick={() => onCopyDocumentsClick(item)}
                  title="Copiar Documentos"
                >
                  <i className="fa fa-copy"></i>
                </CButton>
              </PermissionRequiredComponent>
              <PermissionRequiredComponent permissionName={ASSETS_UPDATE}>
                <CButton
                  className="text-white"
                  color="warning"
                  size="sm"
                  onClick={() => onUpdateClick(item)}
                  title="Editar"
                >
                  <i className="fa fa-pencil"></i>
                </CButton>
              </PermissionRequiredComponent>
              <PermissionRequiredComponent permissionName={ASSETS_DESTROY}>
                <CButton
                  className="text-white"
                  color="danger"
                  size="sm"
                  onClick={() => onDeleteClick(item)}
                  title="Eliminar"
                >
                  <i className="fa fa-trash"></i>
                </CButton>
              </PermissionRequiredComponent>
            </CButtonGroup>
          );
        },
      },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
    }),
    []
  );

  const onGridReady = useCallback((params: GridReadyEvent) => {
    params.api.sizeColumnsToFit();
  }, []);

  useEffect(() => {
    fetchAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("assets")) {
        return;
      }
      fetchAssets();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Activos</h3>
              </div>
              <PermissionRequiredComponent permissionName={ASSETS_CREATE}>
                <div className="p-2 ">
                  <CButton
                    color="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    <BsFillPlusCircleFill />
                    &nbsp; Agregar Nuevo
                  </CButton>
                </div>
              </PermissionRequiredComponent>
            </CCardHeader>
            <CCardBody>
              {/*<CRow className="mb-3">
                <CCol>
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Introduzca el termino de busqueda"
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onSearch}
                    >
                      Buscar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>*/}
              <CRow>
                <CCol>
                  <div
                    className="ag-theme-alpine"
                    style={{
                      height: "720px", // Use string instead of number
                      width: "100%",
                    }}
                  >
                    <AgGridReact
                      rowData={assets}
                      columnDefs={columnDefs}
                      defaultColDef={defaultColDef}
                      pagination={true}
                      paginationPageSize={50}
                      onGridReady={onGridReady}
                      loading={loading}
                      localeText={spanishLocaleText}
                      loadingOverlayComponent={() => (
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="sr-only">Cargando...</span>
                        </div>
                      )}
                      noRowsOverlayComponent={() => (
                        <div className="text-center p-4">
                          <h4>Sin Resultados</h4>
                        </div>
                      )}
                      theme="legacy"
                      animateRows={true}
                      rowHeight={50}
                      headerHeight={50}
                      suppressHorizontalScroll={false}
                    />
                  </div>
                  <div className="mt-3 text-muted">
                    Mostrando {assets.length} de {totalItems} registros
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <AssetCopyDocumentsModal
        show={showCopyDocumentsModal}
        onCancel={onAssetCopyDocumentsCancel}
        onSuccess={onAssetCopyDocumentsSuccess}
        asset={asset}
      />
      <AssetUpdateModal
        show={showEditModal}
        onCancel={onAssetEditCancel}
        onSuccess={onAssetEditSuccess}
        asset={asset}
      />
      <AssetDeleteModal
        show={showDeleteModal}
        onCancel={onAssetDeleteCancel}
        onSuccess={onAssetDeleteSuccess}
        asset={asset}
      />
    </>
  );
};

export default AssetList;
